import {
  Box,
  Button,
  Menu,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type RangeFilterMenuProps = {
  anchorEl: HTMLElement | null;
  activeFilter: string;
  filterKey: string;
  minParamKey: string;
  maxParamKey: string;
  min: number;
  max: number;
  defaultRange?: [number, number];
  marks?: { value: number; label: string }[];
  label?: string;
  onClose: () => void;
};

export const RangeFilterMenu: React.FC<RangeFilterMenuProps> = ({
  anchorEl,
  activeFilter,
  filterKey,
  minParamKey,
  maxParamKey,
  min,
  max,
  marks,
  label = 'Select Range',
  defaultRange,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const computedDefaultRange = useMemo<[number, number]>(() => {
    return defaultRange ?? [min, max];
  }, [defaultRange, min, max]);

  const [range, setRange] = useState<[number | '', number | '']>(computedDefaultRange);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const minValue = Number(searchParams.get(minParamKey)) || computedDefaultRange[0];
    const maxValue = Number(searchParams.get(maxParamKey)) || computedDefaultRange[1];
    setRange([
      Math.max(min, Math.min(minValue, max)),
      Math.max(min, Math.min(maxValue, max)),
    ]);
  }, [location.search, minParamKey, maxParamKey, computedDefaultRange, min, max]);

  const handleInputChange = (index: number, value: string) => {
    const updated = [...range] as [number | '', number | ''];
    if (value === '') {
      updated[index] = '';
    } else {
      const num = Number(value);
      if (!isNaN(num)) {
        updated[index] = num;
      }
    }
    setRange(updated);
  };

  const applyFilter = () => {
    const [minValue, maxValue] = range;
    const params = new URLSearchParams(location.search);

    params.set(minParamKey, String(minValue || min));
    params.set(maxParamKey, String(maxValue || max));
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    onClose();
  };

  const clearFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete(minParamKey);
    params.delete(maxParamKey);
    params.delete('page');
    navigate(`${location.pathname}?${params.toString()}`);
    setRange(computedDefaultRange);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl) && activeFilter === filterKey}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 400,
          mt: 1,
          borderRadius: 2,
          boxShadow: 3,
          overflowY: 'visible',
          px: 2,
          py: 2,
        },
      }}
    >
      <Box px={2} py={1} width="100%">
        <Typography gutterBottom fontWeight="bold">
          {label}
        </Typography>

        <Slider
          value={[
            typeof range[0] === 'number' ? Math.min(range[0], max) : min,
            typeof range[1] === 'number' ? Math.min(range[1], max) : max,
          ]}
          onChange={(_, val) => setRange(val as [number, number])}
          valueLabelDisplay="auto"
          min={min}
          max={max}
          step={1000}
          marks={marks}
          sx={{ mt: 3, mb: 2 }}
        />

        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Min"
            type="number"
            value={range[0]}
            onChange={(e) => handleInputChange(0, e.target.value)}
            inputProps={{ min, max }}
          />
          <TextField
            fullWidth
            size="small"
            label="Max"
            type="number"
            value={range[1]}
            onChange={(e) => handleInputChange(1, e.target.value)}
            inputProps={{ min, max }}
          />
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
          <Button size="small" onClick={clearFilter}>
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={applyFilter}
            disabled={range[0] === '' || range[1] === ''}
          >
            Apply
          </Button>
        </Stack>
      </Box>
    </Menu>
  );
};
